import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';

const REFRESH_INTERVAL = 30;

interface Stats {
  totalSessions: number;
  activeSessions: number;
  avgTimeOnPage: number;
  bounceRate: number;
  topSource: string;
  mobileRatio: number;
  lastUpdated: Date;
}

export default function VisitorStatsBar() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [countdown, setCountdown] = useState(REFRESH_INTERVAL);
  const [refreshing, setRefreshing] = useState(false);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchStats = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);

    const { data } = await supabase
      .from('page_events')
      .select('session_id, event_type, device, referrer, extra, created_at');

    if (!data) {
      setRefreshing(false);
      return;
    }

    const sessions = new Map<string, {
      device: string | null;
      referrer: string | null;
      lastSeen: string;
      hasLeave: boolean;
      timeOnPage: number | null;
      eventTypes: Set<string>;
    }>();

    (data as Array<{
      session_id: string;
      event_type: string;
      device: string | null;
      referrer: string | null;
      extra: Record<string, unknown> | null;
      created_at: string;
    }>).forEach((ev) => {
      if (!sessions.has(ev.session_id)) {
        sessions.set(ev.session_id, {
          device: ev.device,
          referrer: ev.referrer,
          lastSeen: ev.created_at,
          hasLeave: false,
          timeOnPage: null,
          eventTypes: new Set(),
        });
      }
      const s = sessions.get(ev.session_id)!;
      s.eventTypes.add(ev.event_type);
      if (ev.created_at > s.lastSeen) s.lastSeen = ev.created_at;
      if (ev.event_type === 'page_leave') {
        s.hasLeave = true;
        s.timeOnPage = (ev.extra?.seconds_on_page as number) ?? null;
      }
    });

    const total = sessions.size;
    const now = Date.now();
    const active = Array.from(sessions.values()).filter(
      (s) => now - new Date(s.lastSeen).getTime() < 5 * 60 * 1000
    ).length;

    const timesOnPage = Array.from(sessions.values())
      .map((s) => s.timeOnPage)
      .filter((t): t is number => t !== null);
    const avgTime = timesOnPage.length > 0
      ? Math.round(timesOnPage.reduce((a, b) => a + b, 0) / timesOnPage.length)
      : 0;

    const bounced = Array.from(sessions.values()).filter(
      (s) => s.eventTypes.size <= 2
    ).length;
    const bounceRate = total > 0 ? Math.round((bounced / total) * 100) : 0;

    const sourceCounts = new Map<string, number>();
    sessions.forEach((s) => {
      let src = 'Direkt';
      if (s.referrer?.includes('google')) src = 'Google';
      else if (s.referrer?.includes('instagram')) src = 'Instagram';
      else if (s.referrer?.includes('linkedin')) src = 'LinkedIn';
      else if (s.referrer?.includes('twitter') || s.referrer?.includes('x.com')) src = 'Twitter/X';
      else if (s.referrer?.includes('facebook')) src = 'Facebook';
      else if (s.referrer) src = 'Diğer';
      sourceCounts.set(src, (sourceCounts.get(src) ?? 0) + 1);
    });
    const topSource = Array.from(sourceCounts.entries()).sort((a, b) => b[1] - a[1])[0]?.[0] ?? '—';

    const mobileCount = Array.from(sessions.values()).filter((s) => s.device === 'mobile').length;
    const mobileRatio = total > 0 ? Math.round((mobileCount / total) * 100) : 0;

    setStats({ totalSessions: total, activeSessions: active, avgTimeOnPage: avgTime, bounceRate, topSource, mobileRatio, lastUpdated: new Date() });
    setRefreshing(false);
    setCountdown(REFRESH_INTERVAL);
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchStats(false);
  }, [fetchStats]);

  // Auto-refresh every 30s + countdown tick
  useEffect(() => {
    const refreshTimer = setInterval(() => {
      fetchStats(true);
    }, REFRESH_INTERVAL * 1000);

    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) return REFRESH_INTERVAL;
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(refreshTimer);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [fetchStats]);

  const handleManualRefresh = () => {
    fetchStats(false);
  };

  const circumference = 2 * Math.PI * 10;
  const dashOffset = circumference - (countdown / REFRESH_INTERVAL) * circumference;

  const cards = stats ? [
    {
      icon: 'ri-group-line',
      label: 'Toplam Ziyaret',
      value: stats.totalSessions.toString(),
      sub: 'benzersiz oturum',
      color: '#a78bfa',
      pulse: false,
    },
    {
      icon: 'ri-radio-button-line',
      label: 'Şu An Sitede',
      value: stats.activeSessions.toString(),
      sub: 'aktif ziyaretçi',
      color: '#4ade80',
      pulse: stats.activeSessions > 0,
    },
    {
      icon: 'ri-time-line',
      label: 'Ort. Süre',
      value: stats.avgTimeOnPage > 0
        ? `${Math.floor(stats.avgTimeOnPage / 60)}dk ${stats.avgTimeOnPage % 60}sn`
        : '—',
      sub: 'sitede geçirilen süre',
      color: '#f0b429',
      pulse: false,
    },
    {
      icon: 'ri-logout-circle-line',
      label: 'Hemen Çıkma',
      value: `%${stats.bounceRate}`,
      sub: 'bounce rate',
      color: stats.bounceRate > 60 ? '#f87171' : '#4ade80',
      pulse: false,
    },
    {
      icon: 'ri-global-line',
      label: 'En Çok Kaynak',
      value: stats.topSource,
      sub: 'en fazla trafik',
      color: '#00d4ff',
      pulse: false,
    },
    {
      icon: 'ri-smartphone-line',
      label: 'Mobil Oran',
      value: `%${stats.mobileRatio}`,
      sub: 'mobil ziyaretçi',
      color: '#fb923c',
      pulse: false,
    },
  ] : [];

  return (
    <div className="space-y-3">
      {/* Auto-refresh status bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {stats?.activeSessions && stats.activeSessions > 0 ? (
            <span className="flex items-center gap-1.5 text-xs font-semibold text-green-400">
              <span className="relative flex w-2 h-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex rounded-full w-2 h-2 bg-green-400" />
              </span>
              {stats.activeSessions} kişi şu an sitede
            </span>
          ) : (
            <span className="text-xs text-gray-600">Şu an aktif ziyaretçi yok</span>
          )}
        </div>

        <div className="flex items-center gap-3">
          {stats && (
            <span className="text-gray-600 text-[10px]">
              Son güncelleme: {stats.lastUpdated.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          )}

          {/* Countdown ring + manual refresh */}
          <button
            type="button"
            onClick={handleManualRefresh}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-semibold transition-all cursor-pointer"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', color: '#6b7280' }}
            title="Şimdi yenile"
          >
            {refreshing ? (
              <i className="ri-loader-4-line text-xs animate-spin" style={{ color: '#f0b429' }} />
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" className="flex-shrink-0">
                <circle cx="12" cy="12" r="10" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="2.5" />
                <circle
                  cx="12" cy="12" r="10"
                  fill="none"
                  stroke="#f0b429"
                  strokeWidth="2.5"
                  strokeDasharray={circumference}
                  strokeDashoffset={dashOffset}
                  strokeLinecap="round"
                  transform="rotate(-90 12 12)"
                  style={{ transition: 'stroke-dashoffset 1s linear' }}
                />
                <text x="12" y="15.5" textAnchor="middle" fontSize="7" fill="#f0b429" fontWeight="bold">{countdown}</text>
              </svg>
            )}
            <span>{refreshing ? 'Yenileniyor...' : `${countdown}sn`}</span>
          </button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {cards.length === 0
          ? Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="rounded-xl p-3 flex flex-col gap-2 animate-pulse"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
              >
                <div className="h-2 w-16 rounded" style={{ background: 'rgba(255,255,255,0.06)' }} />
                <div className="h-5 w-10 rounded" style={{ background: 'rgba(255,255,255,0.06)' }} />
                <div className="h-2 w-14 rounded" style={{ background: 'rgba(255,255,255,0.04)' }} />
              </div>
            ))
          : cards.map((card) => (
              <div
                key={card.label}
                className="rounded-xl p-3 flex flex-col gap-1 transition-all"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
              >
                <div className="flex items-center gap-1.5">
                  {card.pulse ? (
                    <span className="relative flex w-2 h-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                      <span className="relative inline-flex rounded-full w-2 h-2 bg-green-400" />
                    </span>
                  ) : (
                    <i className={`${card.icon} text-xs`} style={{ color: card.color }} />
                  )}
                  <span className="text-gray-500 text-[10px] font-semibold uppercase tracking-wider">{card.label}</span>
                </div>
                <p
                  className="text-lg font-black leading-none transition-all"
                  style={{ color: refreshing ? 'rgba(255,255,255,0.3)' : 'white' }}
                >
                  {card.value}
                </p>
                <p className="text-gray-600 text-[10px]">{card.sub}</p>
              </div>
            ))}
      </div>
    </div>
  );
}
